/**
 * Shared layout primitives for the dashboard's tiles.
 *
 * The card's padding and the full-bleed bands that cancel it have to come from
 * one number. Defining them separately -- a hand-written `1.5rem` on the card
 * against a `2rem` bleed derived from the same "24" -- left the bands
 * overhanging the card edge by 6px on each side.
 */

/**
 * Converts a design's px measurement into rem.
 *
 * Everything in these tiles goes through this: type, spacing, padding, dot and
 * swatch sizes, track geometry. The app pins its root font to 12px and re-bases
 * it at wider viewports, so a hardcoded px value stops scaling with the text
 * around it and a tile's proportions come apart at width.
 */
export const rem = (value: number): string => `${value / 12}rem`;

/**
 * The size and weight every dashboard tile's heading is set in.
 *
 * One constant, not a literal per tile. Written independently they drifted --
 * coverage sat at 18/700 and Cowbell Factors at 15/600 against the 16/700 the
 * other five tiles had settled on, so a column of cards that should read as one
 * set of peers had three different heading sizes down it.
 */
export const TILE_TITLE_FONT_SIZE = rem(18);
export const TILE_TITLE_FONT_WEIGHT = 700;

/**
 * The line directly under a tile's title -- a policy reference, a standfirst, an
 * empty-state message. Same three values everywhere, and the same 2 of gap, so
 * the pairing reads as one component down the column instead of five near-misses
 * (12.5/13/13.2 at three different inks, over gaps of 3, 6, 8 and 12).
 */
export const TILE_SUBTITLE_FONT_SIZE = rem(13);
export const TILE_SUBTITLE_GAP = rem(2);

/**
 * The one gutter in the product: between tiles and around them, on both axes,
 * for every persona.
 *
 * It was written per-layout before -- `1rem` in four places on the policyholder
 * dashboard, a 16 gutter on the Cowbell one, and a frame of 12 top/right/bottom
 * against 16 on the left -- so no two edges of a dashboard agreed. Everything
 * that spaces a tile from anything now reads this.
 */
export const TILE_GAP_REM = rem(12);

/**
 * The sidebar's collapsed icon-rail width, read by both the rail itself and the
 * cap on the content beside it so the two cannot drift.
 */
export const RAIL_WIDTH = 48;
export const RAIL_WIDTH_REM = rem(RAIL_WIDTH);

/**
 * How long the layout's horizontal moves take, published as
 * `--layout-transition-duration`: the nav collapsing to its rail, the content
 * widening beside it, the Ask Moo sidebar sliding in and the page narrowing to
 * make room. One value, because opening Moo collapses the nav in the same
 * gesture and the two have to move together.
 */
export const LAYOUT_TRANSITION_DURATION = '300ms';

/**
 * The expanded sidebar's width, published as `--sidebar-width`.
 *
 * One value because two disagreed: the container was 12rem while the cap on the
 * content beside it reserved 11.6666666667rem. The container flex-shrank to the
 * smaller figure, but the sidebar's Paper is `position: fixed; width: inherit`
 * and so painted the larger one -- leaving the visible menu edge 4px right of
 * its own container, and every gap measured from it 4px short.
 */
export const SIDEBAR_WIDTH = 144;
export const SIDEBAR_WIDTH_REM = rem(SIDEBAR_WIDTH);

/**
 * The agency rail's width with the broker experience on: 32 design px wider than the sidebar, so "New Business" with
 * its unread dot and count, and "Cowbell Academy" with its external-link mark, each fit on one line. Published over
 * `--sidebar-width` while the rail is mounted, so the page beside it narrows by the same amount.
 */
export const AGENCY_RAIL_WIDTH_REM = rem(SIDEBAR_WIDTH + 32);

/**
 * The expanded sidebar's width in design px, read from the `--sidebar-width` value in force (`"14.6667rem"`): the agency
 * rail publishes a wider one than the default. A value that does not read as rem is the default width.
 */
export function expandedSidebarWidth(cssValue: string): number {
  const match = /^\s*(\d+(?:\.\d+)?)rem\s*$/.exec(cssValue);
  return match ? Number(match[1]) * 12 : SIDEBAR_WIDTH;
}

/**
 * Where a drag of the sidebar's edge snaps between the rail and the expanded
 * sidebar: halfway between their widths, in screen px at the given root font
 * size. Derived from the two widths so a change to either moves it too; the
 * expanded width is the one in force (`expandedSidebarWidth`), as the agency
 * rail widens it.
 */
export const railSnapX = (
  rootFontPx: number,
  expandedWidth: number = SIDEBAR_WIDTH
): number => (((RAIL_WIDTH + expandedWidth) / 2) * rootFontPx) / 12;

/**
 * The tile shadow, per colour scheme, published as `--tile-shadow`.
 *
 * The MuiPaper `elevation6` overrides in both themes read these, so a tile that
 * is a Paper and a tile that is a plain section cannot end up with different
 * shadows -- which is exactly what had happened on Insights, where the tiles
 * are sections and carried no shadow at all.
 */
export const TILE_SHADOW = '0 3px 6px 0 rgba(0, 0, 0, 0.16)';
export const TILE_SHADOW_DARK = '0 3px 4px 0 rgba(0, 0, 0, 0.32)';

/** The corner radius every tile shares, published as `--tile-radius`. */
// 4 design px (0.333rem at the 12px root), set 2026-10-06. Mantine controls keep their own default radius.
export const TILE_RADIUS_REM = rem(4);

/** The dashboard card's padding, as a design measurement. */
export const CARD_PADDING = 24;

/** Apply to the card itself. */
export const CARD_PADDING_REM = rem(CARD_PADDING);

/**
 * Cancels the card's padding so a band reaches both card edges, then re-applies
 * it inside so the band's content stays aligned with everything above it.
 */
export const fullBleed = {
  marginLeft: `-${CARD_PADDING / 12}rem`,
  marginRight: `-${CARD_PADDING / 12}rem`,
  paddingLeft: CARD_PADDING_REM,
  paddingRight: CARD_PADDING_REM,
};
